import * as THREE from '../three'

export class SimplePoseSmoother {
  constructor() {
    this.capacity = 16
    this.head = 0
    this.count = 0
    this.delayMs = 140
    this.maxExtrapMs = 120
    this.snaps = Array.from({ length: this.capacity }, () => ({
      t: 0,
      p: new THREE.Vector3(),
      q: new THREE.Quaternion(),
    }))
  }

  push(p, q, tArrival) {
    const slot = this.snaps[this.head]
    slot.t = tArrival ?? performance.now()
    slot.p.fromArray(p)
    slot.q.fromArray(q).normalize()

    this.head = (this.head + 1) % this.capacity
    this.count = Math.min(this.count + 1, this.capacity)
  }

  // get snapshot i steps back (0 = newest)
  get(i) {
    const idx = (this.head - 1 - i + this.capacity) % this.capacity
    return this.snaps[idx]
  }

  sample(outP, outQ) {
    if (this.count < 2) return false

    const now = performance.now()
    const tRender = now - this.delayMs

    // Walk back until we find the first snap older than tRender
    let i0 = -1,
      i1 = -1
    for (let i = 0; i < this.count - 1; i++) {
      const newer = this.get(i)
      const older = this.get(i + 1)
      if (older.t <= tRender && tRender <= newer.t) {
        i0 = i + 1
        i1 = i
        break
      }
    }

    if (i0 !== -1 && i1 !== -1) {
      const s0 = this.get(i0)
      const s1 = this.get(i1)
      const u = (tRender - s0.t) / Math.max(1e-6, s1.t - s0.t)
      outP.copy(s0.p).lerp(s1.p, u)
      outQ
        .copy(s0.q)
        .slerp(s1.q, THREE.MathUtils.clamp(u, 0, 1))
        .normalize()
      return true
    }

    // Extrapolation case
    const newest = this.get(0)
    const prev = this.get(1)
    const dt = newest.t - prev.t || 1
    const v = newest.p
      .clone()
      .sub(prev.p)
      .multiplyScalar(1 / dt)

    const dtPred = Math.min(this.maxExtrapMs, tRender - newest.t)
    outP.copy(newest.p).addScaledVector(v, dtPred)

    outQ
      .copy(prev.q)
      .slerp(newest.q, 1.0 + dtPred / dt)
      .normalize()
    return true
  }
}
